import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { DownloadSimple, FileText } from "@phosphor-icons/react";
import { downloadBlob, downloadJsonFile } from "../../lib/downloads";
import { useRegulatoryAttachments } from "./RegulatoryAttachments";
import {
  DevicePicker, Field, NoProjectDevices, SectionCard, SelectField,
} from "./RegulatoryShared";

const RISK_CLASSES = ["I", "IIa", "IIb", "III"];

function simpleClassForDevice(value) {
  value = value || "";
  if (["I", "Is", "Im", "Ir"].includes(value)) return "I";
  return RISK_CLASSES.includes(value) ? value : "";
}

export default function CeMarkingTab({ project, devices = [], role = "manufacturer", onChange, onEditCompany }) {
  const t = useT();
  const [deviceId, setDeviceId] = useState(devices[0]?.id || "");
  const device = devices.find((item) => item.id === deviceId);
  const currentUdi = device?.udi_di || "";
  const currentProductName = device?.name || "";
  const currentDeviceClass = device?.mdr_class || "";
  const [udiCode, setUdiCode] = useState(device?.udi_di || "");
  const [labelFile, setLabelFile] = useState(null);
  const [productName, setProductName] = useState(device?.name || "");
  const [riskClass, setRiskClass] = useState(simpleClassForDevice(device?.mdr_class));
  const [signature, setSignature] = useState(null);
  const [signatureChoice, setSignatureChoice] = useState("");
  const [savingMarking, setSavingMarking] = useState(false);
  const [generatingDeclaration, setGeneratingDeclaration] = useState(false);
  const [exporting, setExporting] = useState("");
  const [exportError, setExportError] = useState("");
  const [markingError, setMarkingError] = useState("");
  const [markingMessage, setMarkingMessage] = useState("");
  const [declarationError, setDeclarationError] = useState("");
  const attachments = useRegulatoryAttachments(project.id, deviceId, "ce_marking", onChange);
  const labelDocuments = attachments.documents.filter((item) => item.record_id === "ce_label");
  const latestLabel = labelDocuments[0];
  const roleTitleKey = role === "importer"
    ? "reg.ce.title.importer"
    : role === "distributor"
      ? "reg.ce.title.distributor"
      : "reg.ce.title";
  const roleSubtitleKey = role === "importer"
    ? "reg.ce.simple_subtitle.importer"
    : role === "distributor"
      ? "reg.ce.simple_subtitle.distributor"
      : "reg.ce.simple_subtitle";

  useEffect(() => {
    if (!devices.some((item) => item.id === deviceId)) setDeviceId(devices[0]?.id || "");
  }, [devices, deviceId]);

  useEffect(() => {
    setUdiCode(currentUdi);
    setProductName(currentProductName);
    setRiskClass(simpleClassForDevice(currentDeviceClass));
    setLabelFile(null);
    setMarkingMessage("");
    setMarkingError("");
    setDeclarationError("");
  }, [deviceId, currentDeviceClass, currentProductName, currentUdi]);

  useEffect(() => {
    if (role !== "manufacturer") {
      setSignature(null);
      setSignatureChoice("");
      return undefined;
    }
    let active = true;
    api.get("/user/signature")
      .then(({ data }) => {
        if (!active) return;
        setSignature(data?.has_signature ? data : null);
        setSignatureChoice(data?.has_signature ? "company_saved" : "");
      })
      .catch(() => {
        if (active) {
          setSignature(null);
          setSignatureChoice("");
        }
      });
    return () => { active = false; };
  }, [role]);

  async function saveMarking() {
    if (!device) return;
    const fileToUpload = labelFile;
    if (fileToUpload && fileToUpload.size > 5 * 1024 * 1024) {
      setMarkingError(t("reg.file_too_large"));
      return;
    }
    setSavingMarking(true);
    setMarkingError("");
    setMarkingMessage("");
    try {
      const { data } = await api.put(`/devices/${device.id}/udi`, { udi_di: udiCode.trim() });
      setUdiCode(data.udi_di || "");
      await onChange?.();
      if (fileToUpload) {
        const uploaded = await attachments.upload("ce_label", fileToUpload);
        if (!uploaded) {
          setMarkingError(t("reg.ce.udi_saved_upload_failed"));
          return;
        }
        setLabelFile(null);
      }
      setMarkingMessage(t("reg.ce.saved"));
    } catch (e) {
      setMarkingError(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingMarking(false);
    }
  }

  async function generateDeclaration() {
    if (!device || !signature || signatureChoice !== "company_saved") return;
    setGeneratingDeclaration(true);
    setDeclarationError("");
    try {
      const { data } = await api.post(
        `/projects/${project.id}/devices/${device.id}/ce-marking/declaration.pdf`,
        {
          product_name: productName.trim(),
          risk_class: riskClass,
          signature_source: "company_saved",
          lang: localStorage.getItem("rg_lang") === "es" ? "es" : "en",
        },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(data);
      const anchor = window.document.createElement("a");
      anchor.href = url;
      anchor.download = `RA-EU-DoC-DRAFT_${device.id.slice(0, 8)}.pdf`;
      anchor.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setDeclarationError(formatApiError(e.response?.data?.detail) || t("reg.ce.generate_error"));
    } finally {
      setGeneratingDeclaration(false);
    }
  }

  async function downloadExport(kind, format) {
    if (!device) return;
    const key = `${kind}-${format}`;
    setExporting(key);
    setExportError("");
    try {
      const lang = localStorage.getItem("rg_lang") === "es" ? "es" : "en";
      const route = kind === "udi"
        ? `/projects/${project.id}/devices/${device.id}/exports/udi.${format}`
        : `/projects/${project.id}/devices/${device.id}/exports/operator-evidence.${format}`;
      const params = kind === "udi" ? { lang } : { role_view: role, lang };
      const { data } = await api.get(route, { params, responseType: "blob" });
      const prefix = kind === "udi" ? "RA-UDI-SOURCE" : "RA-EVIDENCE";
      downloadBlob(data, `${prefix}_${device.id.slice(0, 8)}.${format}`);
    } catch (e) {
      setExportError(formatApiError(e.response?.data?.detail) || t("reg.ce.export_error"));
    } finally {
      setExporting("");
    }
  }

  function downloadDeclarationSourceJson() {
    downloadJsonFile({
      schema: "airegulatory.eu-declaration-draft-source.v1",
      document_type: "eu_declaration_of_conformity_draft_source",
      generated_at: new Date().toISOString(),
      project: { id: project.id, name: project.name },
      device: { id: device.id, name: device.name, model: device.model },
      product_name: productName.trim(),
      risk_class: riskClass,
      signature_source: "company_saved",
      saved_signature_available: Boolean(signature),
      status: "draft_not_approved",
      limitations: [
        "Este JSON contiene datos de origen del borrador; no es una Declaración UE de Conformidad emitida.",
        "El fabricante legalmente responsable debe completar y verificar los datos y el texto del Anexo IV antes de aprobarlo.",
        "La aplicación no envía ni registra esta información en EUDAMED.",
      ],
    }, `RA-EU-DoC-DRAFT-SOURCE_${device.id.slice(0, 8)}.json`);
  }

  if (!devices.length) return <NoProjectDevices projectId={project.id} />;
  if (!device) return null;

  return (
    <div className="space-y-4" data-testid="ce-marking-module">
      <header className="grid-card flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="ce-device-context">
        <div className="flex items-start gap-2">
          <FileText size={20} weight="duotone" className="mt-0.5 shrink-0 text-blue-700" />
          <div>
            <h2 className="text-lg font-extrabold tracking-tight">{t(roleTitleKey)}</h2>
            <p className="mt-1 text-sm text-zinc-500">{t(roleSubtitleKey)}</p>
          </div>
        </div>
        {devices.length > 1 ? (
          <div className="sm:w-80">
            <DevicePicker devices={devices} value={deviceId} onChange={setDeviceId} testid="ce-device-picker" />
          </div>
        ) : (
          <div className="text-xs text-zinc-500 sm:text-right" data-testid="ce-current-device">
            <span className="block font-mono uppercase tracking-wider">{t("reg.select_device")}</span>
            <span className="block font-semibold text-zinc-700">{device.name}</span>
          </div>
        )}
      </header>

      <section className="grid-card space-y-4 p-5 sm:p-6" data-testid="ce-marking-simple">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block min-w-0">
            <span className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">
              {t("reg.ce.label_file")}
            </span>
            <input
              key={deviceId}
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
              onChange={(event) => setLabelFile(event.target.files?.[0] || null)}
              data-testid="ce-label-file"
              className="block w-full text-xs text-zinc-600 file:mr-3 file:border file:border-zinc-200 file:bg-white file:px-3 file:py-2 file:text-xs file:font-semibold hover:file:bg-zinc-50"
            />
            <span className="block text-[10px] text-zinc-400 mt-1">{t("reg.file_rules")}</span>
            {latestLabel && (
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                <span className="text-zinc-600 break-all">{t("reg.ce.saved_label")}: {latestLabel.file_name}</span>
                <button
                  type="button"
                  onClick={() => attachments.download(latestLabel)}
                  disabled={attachments.busy === latestLabel.id}
                  className="inline-flex items-center gap-1 text-blue-700 hover:underline disabled:opacity-50"
                  data-testid="ce-label-download"
                >
                  <DownloadSimple size={13} /> {t("reg.download")}
                </button>
              </div>
            )}
          </label>
          <Field
            label={t("reg.ce.udi_code")}
            value={udiCode}
            onChange={setUdiCode}
            placeholder={t("reg.ce.udi_placeholder")}
            testid="ce-udi-code"
          />
        </div>
        {(markingError || attachments.error) && (
          <p className="mt-3 text-xs text-red-700" role="alert" data-testid="ce-marking-error">
            {markingError || attachments.error}
          </p>
        )}
        {markingMessage && <p className="mt-3 text-xs text-emerald-700" role="status">{markingMessage}</p>}
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={saveMarking}
            disabled={savingMarking || attachments.busy !== ""}
            className="btn-primary px-4 py-2 text-xs disabled:opacity-40"
            data-testid="ce-save-simple"
          >
            {savingMarking ? t("reg.saving") : t("reg.save")}
          </button>
        </div>
      </section>

      {role === "manufacturer" && <SectionCard title={t("reg.ce.declaration_title")} subtitle={t("reg.ce.declaration_simple_help")} testid="ce-declaration-section">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field
            label={t("reg.ce.product_name")}
            value={productName}
            onChange={setProductName}
            testid="ce-product-name"
          />
          <SelectField
            label={t("reg.ce.risk_class")}
            value={riskClass}
            onChange={setRiskClass}
            testid="ce-risk-class"
            options={[
              ["", t("reg.ce.select_class")],
              ["I", t("reg.ce.class_i")],
              ["IIa", t("reg.ce.class_iia")],
              ["IIb", t("reg.ce.class_iib")],
              ["III", t("reg.ce.class_iii")],
            ]}
          />
          <SelectField
            label={t("reg.ce.signature_select")}
            value={signatureChoice}
            onChange={setSignatureChoice}
            testid="ce-signature-select"
            options={[
              ["", signature ? t("reg.ce.select_signature") : t("reg.ce.signature_missing")],
              ...(signature ? [["company_saved", t("reg.ce.saved_company_signature")]] : []),
            ]}
          />
        </div>
        {signatureChoice === "company_saved" && signature?.data_url && (
          <div className="ce-signature-paper mt-3 flex items-center gap-3 rounded-sm p-3" data-testid="ce-signature-preview">
            <img src={signature.data_url} alt={t("reg.ce.saved_company_signature")} className="max-h-12 max-w-48 object-contain" />
            <span className="text-xs text-zinc-600">{t("reg.ce.signature_preview_note")}</span>
          </div>
        )}
        {!signature && (
          <div className="mt-3 text-xs text-amber-800">
            {t("reg.ce.signature_missing_help")}{" "}
            {onEditCompany && (
              <button type="button" onClick={onEditCompany} className="font-semibold underline" data-testid="ce-edit-company-signature">
                {t("reg.ce.open_company")}
              </button>
            )}
          </div>
        )}
        <p className="mt-3 text-xs text-amber-800">{t("reg.ce.draft_disclaimer")}</p>
        {declarationError && <p className="mt-3 text-xs text-red-700" role="alert" data-testid="ce-error">{declarationError}</p>}
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <button type="button" onClick={downloadDeclarationSourceJson}
            disabled={!productName.trim() || !riskClass || !signature || signatureChoice !== "company_saved"}
            className="border border-zinc-300 bg-white px-4 py-2 text-xs font-semibold text-zinc-700 hover:bg-zinc-50 disabled:opacity-40"
            data-testid="ce-declaration-json">
            {t("reg.ce.declaration_json")}
          </button>
          <button
            type="button"
            onClick={generateDeclaration}
            disabled={generatingDeclaration || !productName.trim() || !riskClass || !signature || signatureChoice !== "company_saved"}
            className="btn-primary px-4 py-2 text-xs disabled:opacity-40"
            data-testid="ce-generate-download"
          >
            {generatingDeclaration ? t("reg.downloading") : t("reg.ce.generate_download")}
          </button>
        </div>
      </SectionCard>}

      {role === "manufacturer" && (
        <SectionCard title={t("reg.ce.udi_export_title")} subtitle={t("reg.ce.udi_export_help")} testid="ce-udi-export">
          <p className="mb-3 text-xs text-[var(--text-2)]">{t("reg.ce.export_saved_only")}</p>
          {exportError && <p className="mb-3 text-xs text-[var(--risk-danger)]" role="alert">{exportError}</p>}
          <div className="flex flex-wrap gap-2">
            {["pdf", "json"].map((format) => {
              const key = `udi-${format}`;
              return (
                <button key={format} type="button" onClick={() => downloadExport("udi", format)}
                  disabled={exporting !== ""} data-testid={`ce-udi-export-${format}`}
                  className="inline-flex items-center gap-1.5 rounded-sm border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-xs font-semibold text-[var(--text)] transition-colors hover:bg-[var(--surface-hover)] disabled:opacity-40">
                  {format === "pdf" ? <FileText size={14} /> : <span className="font-mono text-[10px]">JSON</span>}
                  {exporting === key ? t("reg.ce.exporting") : t(`reg.ce.export_${format}`)}
                </button>
              );
            })}
          </div>
        </SectionCard>
      )}

      {role !== "manufacturer" && (
        <SectionCard title={t("reg.ce.evidence_export_title")} subtitle={t("reg.ce.evidence_export_help")} testid="ce-operator-evidence-export">
          <p className="mb-3 text-xs text-[var(--text-2)]">{t("reg.ce.evidence_disclaimer")}</p>
          {exportError && <p className="mb-3 text-xs text-[var(--risk-danger)]" role="alert">{exportError}</p>}
          <div className="flex flex-wrap gap-2">
            {["pdf", "json"].map((format) => {
              const key = `evidence-${format}`;
              return (
                <button key={format} type="button" onClick={() => downloadExport("evidence", format)}
                  disabled={exporting !== ""} data-testid={`ce-evidence-export-${format}`}
                  className="inline-flex items-center gap-1.5 rounded-sm border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-xs font-semibold text-[var(--text)] hover:bg-[var(--surface-hover)] disabled:opacity-40">
                  {format === "pdf" ? <FileText size={14} /> : <span className="font-mono text-[10px]">JSON</span>}
                  {exporting === key ? t("reg.ce.exporting") : t(`reg.ce.export_${format}`)}
                </button>
              );
            })}
          </div>
        </SectionCard>
      )}

    </div>
  );
}
